import { API_BASE, withDhtUserId } from './api';
import { storeAuthRole } from './auth';

export const normalizeGdprOrganizations = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }

  const normalized = data
    .map((org) => {
      const latestVersion = Array.isArray(org?.versions) ? org.versions[0] : null;
      const id = typeof org?.GDPROrg_Id === 'string' ? org.GDPROrg_Id.trim() : org?.GDPROrg_Id;
      const name =
        latestVersion?.GDPROrg_Name ||
        latestVersion?.GDPROrg_FullName ||
        org?.GDPROrg_Name ||
        id;

      if (!id) {
        return null;
      }

      return {
        id: String(id).trim(),
        name: String(name || id).trim(),
        raw: org
      };
    })
    .filter(Boolean);

  const seen = new Set();
  return normalized.filter((org) => {
    const dedupeKey = org.id.toLowerCase();
    if (seen.has(dedupeKey)) {
      return false;
    }
    seen.add(dedupeKey);
    return true;
  });
};

export const normalizeGdprRoles = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }

  const normalized = data
    .map((role) => {
      const rawId = role?.GDPROrgRole_Id || role?.GDPRRole_Id;
      const id = typeof rawId === 'string' ? rawId.trim() : rawId;
      const name = role?.GDPROrgRole_Name || role?.GDPRRole_Name || id;

      if (!id) {
        return null;
      }

      return {
        id: String(id).trim(),
        name: String(name || id).trim(),
        raw: role
      };
    })
    .filter(Boolean);

  const seen = new Set();
  return normalized.filter((role) => {
    const dedupeKey = role.id.toLowerCase();
    if (seen.has(dedupeKey)) {
      return false;
    }
    seen.add(dedupeKey);
    return true;
  });
};

const hasAdminSignal = (value) => String(value || '').toLowerCase().includes('admin');

const parseStoredJson = (key) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const parseResponseJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const getFirstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

export const getSelectedRoleDetails = (allRoles, selectedRoleValue) => {
  const normalizedSelectedValue = String(selectedRoleValue || '').trim().toLowerCase();

  const record = allRoles.find((role) => {
    const candidates = [
      role?.id,
      role?.name,
      role?.raw?.GDPROrgRole_Id,
      role?.raw?.GDPRRole_Id,
      role?.raw?.GDPROrgRole_Name,
      role?.raw?.GDPRRole_Name
    ];

    return candidates.some((candidate) => String(candidate || '').trim().toLowerCase() === normalizedSelectedValue);
  }) || null;

  const roleName = String(
    record?.name ||
    record?.raw?.GDPROrgRole_Name ||
    record?.raw?.GDPRRole_Name ||
    selectedRoleValue ||
    ''
  ).trim();

  return { record, roleName };
};

export const resolveAppRoleFromSelection = (roleRecord, selectedRoleValue, selectedRoleName = '') => {
  const roleHints = [
    selectedRoleName,
    roleRecord?.name,
    roleRecord?.id,
    roleRecord?.raw?.GDPROrgRole_Name,
    roleRecord?.raw?.GDPRRole_Name,
    roleRecord?.raw?.GDPROrgRole_Id,
    roleRecord?.raw?.GDPRRole_Id,
    selectedRoleValue
  ];

  if (roleHints.some(hasAdminSignal)) {
    return 'admin';
  }

  if (typeof roleRecord?.raw?.isAdmin === 'boolean') {
    return roleRecord.raw.isAdmin ? 'admin' : 'user';
  }

  if (typeof roleRecord?.raw?.GDPROrgRole_IsAdmin === 'boolean') {
    return roleRecord.raw.GDPROrgRole_IsAdmin ? 'admin' : 'user';
  }

  return 'user';
};

export const saveOrganizationRoleSelection = async ({ organizations, roles, selectedOrg, selectedRole }) => {
  localStorage.setItem('selectedOrganization', selectedOrg);
  localStorage.setItem('selectedRole', selectedRole);

  const selectedRoleDetails = getSelectedRoleDetails(roles, selectedRole);
  const selectedRoleRecord = selectedRoleDetails.record;
  const selectedRoleName = selectedRoleDetails.roleName;
  const resolvedAppRole = resolveAppRoleFromSelection(selectedRoleRecord, selectedRole, selectedRoleName);
  storeAuthRole(resolvedAppRole);

  if (selectedRoleName) {
    localStorage.setItem('selectedRoleName', selectedRoleName);
  }

  const selectedOrganizationRecord = organizations.find(
    (organization) => String(organization.id) === String(selectedOrg)
  );

  if (selectedOrganizationRecord) {
    localStorage.setItem('selectedOrganizationName', selectedOrganizationRecord.name || '');
  }

  const dhtUser = parseStoredJson('dhtUser') || {};
  const authUser = parseStoredJson('authUser') || {};
  const appUserId = getFirstValue(dhtUser.DHTAPPUSR_id, dhtUser.DHTAPPUSRid, dhtUser.DHTAPPUSR_Id);
  const dhtUserId = getFirstValue(dhtUser.DHTUSR_id, dhtUser.DHTUSRid);
  const gdprUserId = getFirstValue(dhtUser.GDPR_Usr_Id, dhtUser.GDPRUsr_Id, authUser.GDPR_Usr_Id, authUser.sub, authUser.id);
  const organizationName = selectedOrganizationRecord?.name || '';

  const payload = {
    DHTAPPUSR_id: appUserId,
    DHTAPPUSRid: appUserId,
    DHTUSRid: dhtUserId,
    DHTUSR_id: dhtUserId,
    GDPR_Usr_Id: gdprUserId,
    userId: getFirstValue(appUserId, dhtUserId, gdprUserId),
    username: authUser.username || dhtUser.username,
    email: authUser.email || dhtUser.email || dhtUser.GDPRUsr_Email,
    organizationId: selectedOrg,
    organisationId: selectedOrg,
    GDPROrg_Id: selectedOrg,
    organizationName,
    organisationName: organizationName,
    roleId: selectedRole,
    GDPROrgRole_Id: selectedRole,
    roleName: selectedRoleName
  };

  const endpoints = [
    `${API_BASE}/user/selections`,
    `${API_BASE}/user-organisations`,
    `${API_BASE}/user-organizations`
  ];

  let lastError = null;
  let didSaveSelection = false;

  for (const endpoint of endpoints) {
    let localSelectionResponse;

    try {
      localSelectionResponse = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(withDhtUserId(payload))
      });
    } catch (error) {
      lastError = error;
      continue;
    }

    const body = await parseResponseJson(localSelectionResponse);

    if (localSelectionResponse.ok) {
      didSaveSelection = true;
      break;
    }

    lastError = new Error(
      body?.error ||
      body?.message ||
      `Local organisation save failed (${localSelectionResponse.status})`
    );

    if (![404, 405].includes(localSelectionResponse.status)) {
      break;
    }
  }

  if (!didSaveSelection) {
    console.warn('Selection sync failed; using local selection only.', lastError);
  }

  return {
    resolvedAppRole,
    selectedRoleName,
    selectedOrganizationName: selectedOrganizationRecord?.name || ''
  };
};
